<template>
  <div :class="$options.name" v-bind="attrs.binds">
    <slot />
  </div>
</template>
<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, GoogleMap, LatLng, wrapComponentElementForGoogle, type InfoBoxOptions, type InfoBoxOverlay, type MarkerOverlay } from 'tg-map-core'
import { defineComponent } from 'vue'
import { TgMap } from '..'
import { eventProp, findAncestorComponentByType, optionalProp, requiredProp, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import MapMixin from '../map-mixin'
import TgMarker, { type TgMarkerPublicInstance } from './TgMarker.vue'
type TgInfoBoxEmits = {
  'update:show': boolean
}
const TgInfoBox = defineComponent({
  name: 'tg-info-box',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: {
    show: requiredProp(Boolean),
    position: optionalProp(LatLng),
    offset: optionalProp(Object),
    maxWidth: optionalProp(Number),
    borderClass: optionalProp(String),
    zIndex: optionalProp(Number),
    disableAutoPan: optionalProp(Boolean),
    'onUpdate:show': eventProp(),
  } satisfies Props<Omit<InfoBoxOptions, 'content'> & {
    show: boolean
  }> & EventProps<TgInfoBoxEmits>,
  emits: undefined as any as EventEmits<TgInfoBoxEmits>,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      overlay: lateinit<InfoBoxOverlay>(),
    }
  },
  watch: {
    show(isShow: boolean) {
      if (this.$marker()) {
        this.$marker()!.onInfoShowChanged(this, isShow)
      } else {
        isShow ? this.overlay.open() : this.overlay.close()
      }
    },
    position(value) {
      this.overlay.setPosition(value)
    },
    offset() {
      this.recreate()
    },
    maxWidth(value) {
      this.overlay.setMaxWidth(value)
    },
    borderClass() {
      this.recreate()
    },
    zIndex(value) {
      this.overlay.setZIndex(value)
    },
    disableAutoPan() {
      this.recreate()
    },
  },
  onCreate() {
    this.overlay = this.$map.createInfoBox(this.getOptions())
    if (this.$marker()) {
      this.$marker()!.onAddInfo(this)
    } else {
      this.show && this.overlay.open()
    }

    if (this.attrs.listenerProps['update:show']) {
      this.overlay.addEventListener('closeclick', () => {
        this.$emit('update:show', false)
      })
    }

    bindEvents(this.attrs.listeners, this.overlay, this.$emit)
  },
  onDestroy() {
    if (this.$marker()) {
      this.$marker()!.onRemoveInfo(this)
    } else {
      this.overlay.close()
    }
  },
  methods: {
    getOptions(): InfoBoxOptions {
      return {
        content: this.content(),
        position: this.position,
        offset: this.offset,
        zIndex: this.zIndex,
        maxWidth: this.maxWidth,
        borderClass: this.borderClass,
        disableAutoPan: this.disableAutoPan,
      }
    },
    content(): HTMLElement {
      if (this.$map instanceof GoogleMap) {
        return wrapComponentElementForGoogle(this.$el, 'google-info-container')
      }
      return this.$el as HTMLElement
    },
    $marker(): TgMarkerPublicInstance | undefined {
      return findAncestorComponentByType(TgMarker, this.$parent, TgMap)
    },
    open(marker?: MarkerOverlay) {
      this.overlay.open(marker)
    },
    close() {
      this.overlay.close()
    },
  },
})
type TgInfoBox = Omit<InstanceType<typeof TgInfoBox>, '$options'>
export default TgInfoBox
</script>
<style lang="scss">
// 只有移到.tg-map__map里面时才显示
.tg-map .tg-info-box {
  display: none;
}

.tg-map__map .tg-info-box {
  display: block;
}
</style>
